import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ServerDataTable, type PagyMeta } from '@/pages/People/_components/ServerDataTable'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import type CrmCoreTeam from '@/types/serializers/CrmCoreTeam'
import type { SharedProps } from '@/types'
import { usePage } from '@inertiajs/react'
import { campaignColumns } from './_components/CampaignColumns'
import { CreateCampaignSheet } from './_components/CreateCampaignSheet'
import { EditCampaignSheet } from './_components/EditCampaignSheet'
import type { AssignableTeam } from './_components/TeamGroupedUserPicker'
import { useSelectedRow } from '@/lib/useSelectedRow'

interface Props {
  campaigns: CrmFundraisingCampaign[]
  pagy: PagyMeta
  q?: Record<string, string>
  teams?: CrmCoreTeam[]
  assignable_teams?: AssignableTeam[]
}

export default function CampaignsIndex({
  campaigns,
  pagy,
  q,
  teams,
  assignable_teams,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const { selected, select, clear } = useSelectedRow('/fundraising/campaigns')
  // Derive the editable selection only from a non-system campaign on the page —
  // a bare ?selected=<id> must not open the edit sheet for a system campaign
  // (the row-click path already skips `row.system`).
  const selectedCampaign = selected
    ? campaigns.find((c) => String(c.id) === selected.id && !c.system)
    : undefined
  const selectedId = selectedCampaign?.id ?? null
  const { auth } = usePage<{ auth: SharedProps['auth'] }>().props

  const isAdmin = auth.user?.role === 'space_admin'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Campagnes</h1>
          <p className="text-muted-foreground">
            Gestion des campagnes de collecte et de sensibilisation.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle campagne
          </Button>
        )}
      </div>

      <ServerDataTable
        columns={campaignColumns}
        data={campaigns}
        pagy={pagy}
        basePath="/fundraising/campaigns"
        selectedId={selectedId}
        onRowClick={
          isAdmin
            ? (row) => {
                if (!row.system) select(row.id)
              }
            : undefined
        }
      />

      {isAdmin && (
        <>
          <CreateCampaignSheet
            open={createOpen}
            onOpenChange={setCreateOpen}
            teams={teams ?? []}
            assignableTeams={assignable_teams ?? []}
          />
          <EditCampaignSheet
            campaignId={selectedId}
            onOpenChange={(open) => {
              if (!open) clear()
            }}
            teams={teams ?? []}
            assignableTeams={assignable_teams ?? []}
          />
        </>
      )}
    </div>
  )
}
